import { ScreenManager, ScreenManagerEvents, ScreenTransitionDirection } from "../../managers/ScreenManager";
import { Showable } from "../../mixins/Showable";
import { ButtonBase, ButtonEvents } from "../../ui/ButtonBase";
import { ApplyMixins } from "../../utils/ApplyMixins";
import { ApplyScalingBias } from "../../utils/ApplyScalingBias";
import { Padding } from "../../utils/Padding";
import { RectSize } from "../../utils/RectSize";
import { LoaderFileKeys } from "../constant/AppConstants";
import { ChangedItemData, LiveDataChangeEvents, OptionsDataManager } from "../managers/OptionsDataManager";
import { ThemeDataManager } from "../managers/ThemeDataManager";
import { AppLocalStorage } from "../managers/AppLocalStorage";
import { AppSettings } from "../managers/AppSettings";
import { ContentsScrollPane } from "./ContentsScrollPane";
import { LayoutDataManager, OptionsItemKeys } from "../managers/LayoutDataManager";
import { AppAudioPlayer, SoundEffectKeys } from "../../audio/AppAudioPlayer";
import { ScrollPaneEvents } from "../../controls/ScrollPane";
import { AppTimings } from "../data/AppConstants";


export interface ScrollableContentScreenBase extends Showable { }

/**
 * Base class for:
 * @see OptionsScreen
 * @see StatisticsScreen
 * @see RulesScreen
 * @see DealCompleteScreen
 */
export class ScrollableContentScreenBase
    extends Phaser.GameObjects.Container {
    public static SCREEN_WIDTH_PERC = 0.9;

    private static appliedMixins: boolean = false;
    public bg: Phaser.GameObjects.TileSprite;
    public btnPrev: ButtonBase;
    public btnNext: ButtonBase;
    public title: Phaser.GameObjects.Text;
    public contentsScrollPane: ContentsScrollPane;
    public transitionDirection: ScreenTransitionDirection = ScreenTransitionDirection.RIGHT_TO_CENTER;
    /**
     * Title padding has effect on amount of space on sides of title and on where the actual masked content starts
     */
    protected titlePadding = new Padding(0, 40);
    protected scrollPaneWidth: number;
    // reset to this before Show
    private defaultScrollPosition: Phaser.Math.Vector2 = new Phaser.Math.Vector2(0, 0);


    public Init(): void {
        this.visible = false;

        if (!ScrollableContentScreenBase.appliedMixins) {
            ApplyMixins(ScrollableContentScreenBase, [Showable]);
            ScrollableContentScreenBase.appliedMixins = true;
        }

        this.SetTweenProps(
            // showTweenConfig
            {
                onStart: this.OnShowStart.bind(this),
                onComplete: this.OnShowComplete.bind(this),
            },
            // hideTweenConfig
            {
                onStart: this.OnHideStart.bind(this),
                onComplete: this.OnHideComplete.bind(this),
            },
            // showTweenTargetProps
            {}, // remove defaults (alpha transition)
            // hideTweenTargetProps
            {}, // remove defaults (alpha transition)
        );

        // start hidden
        const windowRect: RectSize = ScreenManager.GetWindowRect();
        this.x = -windowRect.width;
    }

    public Build(): void {
        const textShadow: Phaser.Types.GameObjects.Text.TextShadow = {
            color: AppSettings.colors.blackString,
            fill: true,
            blur: 8,
            offsetX: 0,
            offsetY: 4,
        };
        const titleStyle: Phaser.Types.GameObjects.Text.TextStyle = {
            color: AppSettings.colors.whiteString,
            // fontSize: "55px",
            fontSize: "80px",
            align: "center",
            fontFamily: AppSettings.fontFamilies.MontserratBold,
            shadow: textShadow,
            metrics: AppSettings.metrics.montserratBold80,
        };

        // expect contentsScrollPane be set in subclass 
        // (ContentsScrollPane is subclassed there)
        this.add(this.contentsScrollPane);

        // btnPrev (button next to content section title)
        this.btnPrev = new ButtonBase(this.scene);
        this.btnPrev.InitBase({
            out: { texture: LoaderFileKeys.ui, frame: "scrollArrow-right-out.png" },
            over: { texture: LoaderFileKeys.ui, frame: "scrollArrow-right-over.png" }
        });
        this.btnPrev.PostInit(40);
        this.btnPrev.setData(
            "baseScale",
            LayoutDataManager.GetSharedData().general.scrollableContent.header.navBtn.scale,
        );
        this.add(this.btnPrev);

        // btnNext (button next to content section title)
        this.btnNext = new ButtonBase(this.scene);
        this.btnNext.InitBase({
            out: { texture: LoaderFileKeys.ui, frame: "scrollArrow-right-out.png" },
            over: { texture: LoaderFileKeys.ui, frame: "scrollArrow-right-over.png" }
        });
        this.btnNext.PostInit(40);
        this.btnNext.setData(
            "baseScale",
            LayoutDataManager.GetSharedData().general.scrollableContent.header.navBtn.scale,
        );
        this.add(this.btnNext);

        // NOTE: we'll update text in UpdateFromLocalizationChange which happens after subclass Build is done (after this)
        this.title = this.scene.make.text({
            style: titleStyle,
        });
        this.title.setOrigin(0.5);
        // this.title.setData("baseScale", 1.0);
        this.add(this.title);

        // perma-listeners
        // listen to data model background change
        // OptionsDataManager.GetEventEmitter().on(LiveDataChangeEvents.BACKGROUND_CHANGE, this.OnBackgroundDataChange, this);
    }

    public UpdateFromVariationChange(): void {

    }

    public UpdateFromLocalizationChange(): void {
        // console.log("UpdateFromLocalizationChange", this.name);

        // update section title data for title on this level
        this.contentsScrollPane.UpdateFromLocalizationChange();
        // update view from the data
        this.title.text = this.contentsScrollPane.GetCurrentContentScrollData().capitalizedTitle;
    }

    protected OnBtnPrevClick(): void {
        AppAudioPlayer.PlaySfx(SoundEffectKeys.click4);

        this.contentsScrollPane.ScrollToPrevContent();
        this.title.text = this.contentsScrollPane.GetCurrentContentScrollData().capitalizedTitle;
    }

    protected OnBtnNextClick(): void {
        AppAudioPlayer.PlaySfx(SoundEffectKeys.click4);

        this.contentsScrollPane.ScrollToNextContent();
        this.title.text = this.contentsScrollPane.GetCurrentContentScrollData().capitalizedTitle;
    }

    protected OnScrollAmountChange(curScrollAmount: Phaser.Math.Vector2) {
        this.title.text = this.contentsScrollPane.GetCurrentContentScrollData().capitalizedTitle;
    }

    protected OnShowStart(animated: boolean): void {
        // console.log("OnShowStart");

        // reset scroll to Y=0
        // reset cur content index to 0 (which also updates title to 1st title)
        // this works by SetScroll calling @see OnScrollAmountChange, 
        // which updates content index from content position, unless already set 
        this.contentsScrollPane.curContentIndex = 0;
        this.contentsScrollPane.fromSetIndexToScrollUpdate = true;
        this.contentsScrollPane.SetScroll(this.defaultScrollPosition);
        this.UpdateFromLocalizationChange(); // this updates title to 1st title

        // refresh scales, positions,..
        this.OnResizeManual();

        // show screen
        this.visible = true;
        this.scene.tweens.killTweensOf(this);
        const scrTransData = ScreenManager.GetScreenTransitionPositions(this.transitionDirection);
        let duration: number = OptionsDataManager.GetTiming(AppTimings.screenShowDuration);
        // duration = 2000;
        if (animated) {
            this.x = scrTransData.startX;
            this.scene.tweens.add({
                targets: this,
                x: scrTransData.endX,
                duration: duration,
                ease: AppSettings.ease.screenShowHideEase,
                onUpdate: () => {
                    // update content mask
                    this.contentsScrollPane.contentMask.UpdateTransforms();
                    // update content items culler
                    // this.contentsScrollPane.UpdateCullItems();
                },
            });
        }
        else {
            this.x = scrTransData.endX;
            this.contentsScrollPane.contentMask.UpdateTransforms();
        }
    }

    protected OnShowComplete(): void {
        // console.log("OnShowComplete");

        this.RegisterListeners();
    }

    protected OnHideStart(animated: boolean): void {
        // console.log("OnHideStart", animated);
        this.UnregisterListeners();

        // hide screen
        this.scene.tweens.killTweensOf(this);
        const scrTransData = ScreenManager.GetScreenTransitionPositions(this.transitionDirection);
        let duration: number = OptionsDataManager.GetTiming(AppTimings.screenHideDuration);
        // duration = 2000;
        if (animated) {
            this.x = scrTransData.startX;
            this.scene.tweens.add({
                targets: this,
                x: scrTransData.endX,
                duration: duration,
                ease: AppSettings.ease.screenShowHideEase,
                onUpdate: () => {
                    // update content mask
                    this.contentsScrollPane.contentMask.UpdateTransforms();
                    // update content items culler
                    // this.contentsScrollPane.UpdateCullItems();
                },
                onComplete: () => {
                    this.visible = false;
                },
            });
        }
        else {
            this.x = scrTransData.endX;
            this.contentsScrollPane.contentMask.UpdateTransforms();
            this.visible = false;
        }
    }

    protected OnHideComplete(): void {
        // override in subclass, for ex  @see DealCompleteScreen
    }

    protected RegisterListeners(): void {
        this.btnPrev.on(ButtonEvents.CLICK, this.OnBtnPrevClick, this);
        this.btnNext.on(ButtonEvents.CLICK, this.OnBtnNextClick, this);
        this.contentsScrollPane.RegisterListeners();
        this.contentsScrollPane.on(ScrollPaneEvents.SCROLL_AMOUNT_CHANGE, this.OnScrollAmountChange, this);
        ScreenManager.GetEventEmitter().on(ScreenManagerEvents.RESIZE, this.OnResize, this);
    }

    protected UnregisterListeners(): void {
        this.btnPrev.off(ButtonEvents.CLICK, this.OnBtnPrevClick);
        this.btnNext.off(ButtonEvents.CLICK, this.OnBtnNextClick);
        this.contentsScrollPane.UnregisterListeners();
        this.contentsScrollPane.off(ScrollPaneEvents.SCROLL_AMOUNT_CHANGE, this.OnScrollAmountChange, this);
        ScreenManager.GetEventEmitter().off(ScreenManagerEvents.RESIZE, this.OnResize);
    }

    public OnResizeManual(): void {
        const windowRect: RectSize = ScreenManager.GetWindowRect();
        this.OnResize(windowRect);
    }

    protected OnResize(windowRect: RectSize): void {
        // console.log("OnResize");

        const screenCenterX: number = (0.5 * windowRect.width) | 0;
        const maxWidth = LayoutDataManager.GetSharedData().general.scrollableContent.size.maxWidth;
        const screenScaleMult: number = ScreenManager.GetScreenScaleMult();
        this.scrollPaneWidth = Math.min(
            windowRect.width * ScrollableContentScreenBase.SCREEN_WIDTH_PERC, 
            maxWidth * screenScaleMult,
        );
        const titleScale: number = ApplyScalingBias(this.scrollPaneWidth / maxWidth, 1.0);
        const navBtnOffsetX = LayoutDataManager.GetSharedData().general.scrollableContent.header.navBtn.offsetX;
        // this.bg.setSize(windowRect.width, windowRect.height);

        // const titleBaseScale = this.title.getData("baseScale");
        // this.title.setScale(titleBaseScale * titleScale);
        this.title.setScale(titleScale);
        const titlePosY = this.titlePadding.top * titleScale + this.title.displayHeight / 2;
        this.title.setPosition(screenCenterX, titlePosY);

        // NOTE: AFTER 'title.setScale'
        const scrollPaneY = this.titlePadding.top * titleScale + this.title.displayHeight + this.titlePadding.bottom * titleScale;

        const btnPrevBaseScale = this.btnPrev.getData("baseScale");
        this.btnPrev.setScale(-btnPrevBaseScale * titleScale, btnPrevBaseScale * titleScale);
        this.btnPrev.setPosition(screenCenterX - navBtnOffsetX * titleScale, titlePosY);

        const btnNextBaseScale = this.btnNext.getData("baseScale");
        this.btnNext.setScale(btnNextBaseScale * titleScale);
        this.btnNext.setPosition(screenCenterX + navBtnOffsetX * titleScale, titlePosY);
        this.contentsScrollPane.setPosition((windowRect.width - this.scrollPaneWidth) / 2, scrollPaneY);
        this.contentsScrollPane.SetSize(this.scrollPaneWidth, windowRect.height - scrollPaneY);
    }
}